import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { TextInput } from "flowbite-react";
import { ModalShell } from "./ModalShell";
import { HiOutlineDocument } from "react-icons/hi";
import { BiTrash } from "react-icons/bi";
import { useUploadFile } from "../../hooks/cdn";
import { PlanTier, usePermissions } from "../../utils/permissions";
import UpgradeModal from "../modals/UpgradeModal";
import { PremiumBenefit } from "./UpgradePromptContent";
import { ACCEPTED_FILE_TYPES } from "../../constants/fileTypes";
import { CustomButton } from "./CustomButton";

interface BusinessCardFileUploadProps {
  onFileUploaded: (
    fileName: string,
    cdnPath: string,
    displayName: string
  ) => void;
  onFileRemoved: (fileName: string) => void;
  uploadedFiles: Array<{
    fileName: string;
    cdnPath: string;
    displayName: string;
  }>;
  maxFiles?: number;
  acceptedTypes?: string;
  maxFileSizeMB?: number;
  usage?: "businessCard" | "askQuestion";
}

const BusinessCardFileUpload: React.FC<BusinessCardFileUploadProps> = ({
  onFileUploaded,
  onFileRemoved,
  uploadedFiles,
  maxFiles = 3,
  acceptedTypes = ACCEPTED_FILE_TYPES,
  maxFileSizeMB = 5,
  usage = "businessCard",
}) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [nameError, setNameError] = useState("");
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [showFileUploadUpgradeModal, setShowFileUploadUpgradeModal] =
    useState(false);

  const handleTriggerFileInput = () => {
    if (uploadedFiles.length >= maxFiles) {
      toast.error(
        t("permissions.businessCard.fileUpload.maxFilesError", {
          max: maxFiles,
        })
      );
      return;
    }
    inputFileRef.current?.click();
  };

  const handleUploadFile = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    if (file.size > maxFileSizeMB * 1048576) {
      toast.error(
        t("permissions.businessCard.fileUpload.fileTooLargeError", {
          maxSizeMB: maxFileSizeMB,
        })
      );
      return;
    }

    if (uploadedFiles.length >= maxFiles) {
      toast.error(
        t("permissions.businessCard.fileUpload.maxFilesError", {
          max: maxFiles,
        })
      );
      return;
    }

    if (uploadedFiles.some((f) => f.fileName === file.name)) {
      toast.error(t("permissions.businessCard.fileUpload.fileExistsError"));
      return;
    }

    setPendingFile(file);
    setDisplayName("");
    setNameError("");
    setShowLabelModal(true);
  };

  const handleConfirmUpload = () => {
    if (!displayName.trim()) {
      setNameError(
        t("permissions.businessCard.fileUpload.displayNameRequired") as string
      );
      return;
    }

    if (displayName.trim().length > 30) {
      setNameError(
        t("permissions.businessCard.fileUpload.displayNameMaxLength") as string
      );
      return;
    }

    if (!pendingFile) return;
    setNameError("");

    uploadFile(
      {
        user_identification: customer_identification,
        entity_identification: user.entity_identification,
        data: { file: pendingFile } as any,
      },
      {
        onSuccess: (res: any) => {
          const cdnPath = res?.data?.element[0]?.cdnPath;
          if (cdnPath) {
            onFileUploaded(pendingFile.name, cdnPath, displayName.trim());
            toast.success(
              t("permissions.businessCard.fileUpload.uploadSuccess")
            );
          }
          if (inputFileRef.current) {
            inputFileRef.current.value = "";
          }
          setPendingFile(null);
          setDisplayName("");
          setShowLabelModal(false);
        },
      }
    );
  };

  const handleCancelUpload = () => {
    setPendingFile(null);
    setDisplayName("");
    setNameError("");
    setShowLabelModal(false);
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  };

  const handleRemoveFile = (fileName: string) => {
    onFileRemoved(fileName);
    toast.success(t("permissions.businessCard.fileUpload.fileRemoved"));
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return "📄";
      case "doc":
      case "docx":
        return "📝";
      case "jpg":
      case "jpeg":
      case "png":
        return "🖼️";
      case "zip":
        return "📦";
      case "csv":
        return "📊";
      case "xlsx":
      case "xls":
        return "📈";
      case "ppt":
      case "pptx":
        return "📺";
      case "txt":
        return "📝";
      default:
        return "📄";
    }
  };

  const attachmentBenefits: PremiumBenefit[] = [
    {
      id: "attachment-file-sharing",
      text: t("dashboard.upgrade.attachment.benefit1") as string,
    },
    {
      id: "attachment-multiple-files",
      text: t("dashboard.upgrade.attachment.benefit2") as string,
    },
    {
      id: "attachment-various-formats",
      text: t("dashboard.upgrade.attachment.benefit3") as string,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 text-center overflow-hidden">
        <div className="grid grid-rows-[auto_1fr_auto] gap-3 items-center max-w-full">
          <HiOutlineDocument
            size={32}
            className="text-blue-400 justify-self-center"
          />
          <div className="text-center space-y-2 max-w-full">
            <p className="text-sm text-gray-700 break-words">
              {t("permissions.businessCard.fileUpload.uploadDescription")}
            </p>
            <p className="text-xs text-gray-500 break-words">
              {t("permissions.businessCard.fileUpload.maxFilesInfo", {
                maxFiles,
                maxSizeMB: maxFileSizeMB,
              })}
            </p>
            <p className="text-xs text-gray-400 break-all px-2">
              {t("permissions.businessCard.fileUpload.allowedFormats", {
                formats: acceptedTypes.replace(/\./g, "").toUpperCase(),
              })}
            </p>
            <CustomButton
              onClick={() => {
                permissions.isAtLeastTier(PlanTier.ENTERPRISE)
                  ? handleTriggerFileInput()
                  : setShowFileUploadUpgradeModal(true);
              }}
              disabled={isUploading || uploadedFiles.length >= maxFiles}
              className="mx-auto"
              isLoading={isUploading}
              title={t("permissions.businessCard.fileUpload.uploadButton")}
              icon={"upload"}
              showUpgrade={!permissions.isAtLeastTier(PlanTier.ENTERPRISE)}
            />
          </div>
        </div>
      </div>

      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            {t("permissions.businessCard.fileUpload.uploadedFilesTitle")}
          </h4>
          <div className="space-y-2">
            {uploadedFiles.map((file, index) => (
              <div
                key={index}
                className="grid grid-cols-[auto_1fr_auto] gap-3 items-center bg-blue-50 p-3 rounded-lg border border-blue-200 overflow-hidden"
              >
                <span className="text-lg flex-shrink-0">
                  {getFileIcon(file.fileName)}
                </span>
                <div className="min-w-0 max-w-full overflow-hidden">
                  <p
                    className="text-sm font-medium text-gray-700 truncate"
                    title={file.displayName}
                  >
                    {file.displayName.length > 30
                      ? `${file.displayName.substring(0, 30)}...`
                      : file.displayName}
                  </p>
                  <p
                    className="text-xs text-gray-400 truncate"
                    title={file.fileName}
                  >
                    {file.fileName}
                  </p>
                  <p className="text-xs text-blue-600 truncate">
                    {t("permissions.businessCard.fileUpload.readyToAdd")}
                  </p>
                </div>
                <CustomButton
                  variant="danger"
                  size="xs"
                  onClick={() => handleRemoveFile(file.fileName)}
                  className="flex-shrink-0"
                  comp={<BiTrash size={14} />}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <input
        type="file"
        hidden
        ref={inputFileRef}
        accept={acceptedTypes}
        onChange={(e) => handleUploadFile(e.target.files)}
      />

      <ModalShell
        show={showLabelModal}
        size="md"
        onClose={handleCancelUpload}
        title={t("permissions.businessCard.fileUpload.modalTitle")}
        footer={
          <>
            <CustomButton
              onClick={handleCancelUpload}
              disabled={isUploading}
              variant="secondary"
              title={t("common.cancel")}
            />
            <CustomButton
              onClick={handleConfirmUpload}
              disabled={isUploading}
              variant="primary"
              isLoading={isUploading}
              title={t("permissions.businessCard.fileUpload.confirmUpload")}
            />
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-700 mb-2">
              {t("permissions.businessCard.fileUpload.selectedFile")}{" "}
              <span className="font-medium break-words">
                {pendingFile?.name}
              </span>
            </p>
            <p className="text-sm text-gray-500 mb-4">
              {usage == "askQuestion"
                ? t(
                    "permissions.businessCard.fileUpload.askQestionLabelDescription"
                  )
                : t("permissions.businessCard.fileUpload.labelDescription")}
            </p>
          </div>
          <TextInput
            type="text"
            color={nameError ? "failure" : undefined}
            placeholder={
              usage === "askQuestion"
                ? (t(
                    "permissions.businessCard.fileUpload.askQuestionLabelPlaceholder"
                  ) as string)
                : (t(
                    "permissions.businessCard.fileUpload.labelPlaceholder"
                  ) as string)
            }
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              if (nameError) setNameError("");
            }}
            autoFocus
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleConfirmUpload();
              }
            }}
          />
          {nameError ? (
            <p className="mt-1 text-xs text-red-600" role="alert">
              {nameError}
            </p>
          ) : (
            displayName.length > 30 && (
              <p className="mt-1 text-xs text-red-600">
                {t(
                  "permissions.businessCard.fileUpload.displayNameLengthCounter",
                  { current: displayName.length }
                )}
              </p>
            )
          )}
        </div>
      </ModalShell>
      <UpgradeModal
        showModal={showFileUploadUpgradeModal}
        setShowModal={setShowFileUploadUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
        title={t("dashboard.upgrade.attachment.title") as string}
        description={t("dashboard.upgrade.attachment.description") as string}
        benefits={attachmentBenefits}
        icon={HiOutlineDocument}
        iconTheme="blue"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />
    </div>
  );
};

export default BusinessCardFileUpload;
